import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { IApiResponse } from "../../../utils/types/api-response";
import {
    IConsentActionPayload,
    IConsentDraftPayload,
    IConsentFormRecord,
} from "../types/client-forms.types";
import {
    recordConsentAction,
    saveConsentDraft,
    uploadConsentAttachments,
} from "../services/client-forms.services";

export function useSaveConsentDraft() {
    return useMutation<
        IApiResponse<unknown>,
        AxiosError,
        { consentId: string; data: IConsentDraftPayload }
    >({
        mutationFn: ({ consentId, data }) => saveConsentDraft(consentId, data),
    });
}

export function useUploadConsentAttachments() {
    return useMutation<
        IApiResponse<unknown>,
        AxiosError<{ message?: string }>,
        { consentId: string; fieldId: string; files: File[] }
    >({
        mutationFn: ({ consentId, fieldId, files }) =>
            uploadConsentAttachments(consentId, fieldId, files),
    });
}

export function useConsentAction() {
    const queryClient = useQueryClient();

    return useMutation<
        IApiResponse<IConsentFormRecord>,
        AxiosError<{ message?: string }>,
        { bookingId: string; data: IConsentActionPayload }
    >({
        mutationKey: ["consent-action"],
        mutationFn: ({ bookingId, data }) =>
            recordConsentAction(bookingId, data),
        onSuccess: async (_response, variables) => {
            await queryClient.invalidateQueries({
                queryKey: ["consent", variables.bookingId],
            });
            await queryClient.invalidateQueries({
                queryKey: ["client-consents"],
            });
        },
    });
}
